import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchGifsQuery } from "queries";
import { TopicSelector } from "components/explore/TopicSelector";
import { GifList, IUserList } from "components/feed/GifList";
import { useQuery } from "hooks/useQuery";
import { ViewRegion } from "utils/metrics";

import "../common.scss";
import "./ExploreGifsPage.scss";
import { IGif, INicheMap } from "services/types";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { filterUsersList } from "utils/filterUsersList";
import { routes } from "routes";
import { ContentPageSkyscraperEnum } from "components/ads/config/units/contentPageSkyscraper";
import { WithSideAds } from "components/ads";

export const ExploreGifsPage = (): JSX.Element => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [order, setOrder] = useState<string>("trending");
  const [tiles, setTiles] = useState<boolean>(true);
  const [verified, setVerified] = useState<boolean>(false);
  const [niches, setNiches] = useState<INicheMap>({});
  const navigate = useNavigate();
  const query = useQuery();

  const { data, isSuccess, isLoading, isError } = useSearchGifsQuery({
    order,
    count: 30,
    page,
    type: "g",
    verified,
  });

  useEffect(() => {
    document.title = "Top Gifs | RedGIFs";
  }, []);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setPage(data.page);
      setPages(data.pages);
      setGifs(prev => filterGifsList(prev, data.gifs));
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [isSuccess, data]);

  useEffect(() => {
    const orderParam = query.get("order");
    const verifiedParam = query.get("verified");

    if (orderParam) {
      changeOrder(orderParam);
    }

    if (verifiedParam) {
      changeVerified(!!verifiedParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    setGifs([]);
    setPage(1);
  }, []);

  const changeOrder = useCallback((value: string): void => {
    const verifiedParam = !!query.get("verified") || verified;

    navigate(routes.exploreTop(value, verifiedParam));
    setOrder(value);
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, verified]);

  const fetchMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const changeTopic = useCallback(() => { }, []);

  const changeMode = useCallback((tiles: boolean) => {
    setTiles(tiles);
  }, []);

  const changeVerified = useCallback((value: boolean) => {
    const orderParam = query.get("order") || order;
    navigate(routes.exploreTop(orderParam, value));
    setVerified(value);
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, order]);

  return (
    <Fragment>
      <WithSideAds
        leftAds={[
          ContentPageSkyscraperEnum.ContentPageSkyscraperL1,
        ]}
        rightAds={[
          ContentPageSkyscraperEnum.ContentPageSkyscraperR1,
        ]}
      >
        <div className="wide page exploreGifsPage">
          <TopicSelector active="gifs" />

          <GifList
            ordering={true}
            orderingTop={true}
            topics={false}
            manage={false}

            topic="Images"
            order={order}
            tiles={tiles}
            verified={verified}
            gifs={gifs}
            users={users}
            niches={niches}
            totalLength={gifs.length}
            hasMore={page < pages}
            changeTopic={changeTopic}
            changeOrder={changeOrder}
            changeMode={changeMode}
            changeVerified={changeVerified}
            fetchMore={fetchMore}
            isError={isError}
            isLoading={isLoading}
            source={ViewRegion.Explore}
          />
        </div>
      </WithSideAds>
    </Fragment>
  );
};
